import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Box } from '@kc-console/common-ui';
import { useServiceMetaQuery } from '@kc-console/context';

import { useLnbCollapsed } from '../../../hooks';
import CollapsedButton from './CollapsedButton';
import LnbServiceTitle from './LnbServiceTitle';
import RecentListSection from './recent/RecentListSection';
import ServiceListSection from './service/ServiceListSection';

/**
 * Lnb 컴포넌트의 props 타입 정의
 */
interface Props {
  /** LNB 비활성화 여부
   * @default false
   */
  hide?: boolean;
}

type LnbContentProps = { collapsed?: boolean };

/**
 * Left Navigation Bar 컴포넌트
 * 서비스 메뉴와 최근 사용 목록을 표시합니다.
 * collapsed => LNB가 접힌 상태
 */
export default function Lnb({ hide = false }: Props) {
  const { data: serviceMeta } = useServiceMetaQuery();
  const { collapsed, toggleCollapsed } = useLnbCollapsed();

  if (hide) return null;

  return (
    <LnbContainer component={'section'} collapsed={collapsed}>
      <LnbServiceTitle service={serviceMeta} collapsed={collapsed} />
      <LnbSectionContainer>
        <ServiceListSection collapsed={collapsed} />
        <RecentListSection collapsed={collapsed} />
      </LnbSectionContainer>
      <CollapsedButton collapsed={collapsed} onCollapse={toggleCollapsed} />
    </LnbContainer>
  );
}

const LnbSectionContainer = styled(Box)`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;

  scrollbar-width: thin; // Firefox를 위한 설정
  scrollbar-color: #cccccc transparent; // Firefox를 위한 설정

  &::-webkit-scrollbar {
    width: 14px;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #cccccc;
    background-clip: padding-box;
    border: 4px solid transparent;
    border-radius: 8px;
  }
`;

const LnbContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'collapsed',
})<LnbContentProps>`
  ${({ collapsed, theme }) => css`
    position: sticky;
    top: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    width: ${collapsed ? 56 : 256}px;
    background-color: ${theme.color['surface/neutral/weak/default']};
  `}
`;
